import { forwardRef } from "react";

import { useMediaUrl } from "../echoflicks/useMediaUrl";
import { useExperimentImageUrl } from "./ExperimentImage";
import type { MediaRef, VideoRef } from "./types";

type ExperimentVideoProps = {
  media: VideoRef | undefined;
  poster?: MediaRef;
  label: string;
  className?: string;
  onClick?: () => void;
  /** A standalone, audible player that loads on play; otherwise the synced transport drives it. */
  controls?: boolean;
};

/**
 * One experiment film, signed through the same `/media/sign` path as every CRM media URL (1 h TTL).
 * By default it has no native controls: the synced transport drives playback so both sides stay
 * in lockstep.
 */
export const ExperimentVideo = forwardRef<
  HTMLVideoElement,
  ExperimentVideoProps
>(({ media, poster, label, className, onClick, controls = false }, ref) => {
  const state = useMediaUrl(
    media ? { storage_bucket: media.bucket, storage_path: media.key } : null,
  );
  const posterUrl = useExperimentImageUrl(poster);
  if (!media) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-xs text-muted-foreground aspect-video ${className ?? ""}`}
      >
        —
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-xs text-destructive p-2 aspect-video ${className ?? ""}`}
      >
        {state.error}
      </div>
    );
  }
  return (
    <video
      ref={ref}
      src={state.url ?? undefined}
      poster={posterUrl ?? undefined}
      aria-label={label}
      preload={controls ? "none" : "metadata"}
      playsInline
      muted={!controls}
      controls={controls}
      className={className}
      onClick={onClick}
    />
  );
});

ExperimentVideo.displayName = "ExperimentVideo";
